import React, { useState } from "react";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import HomeLayout from "../layouts/HomeLayout";
import MetaTags from "../components/MetaTags";
import StyleElement from "../components/StyleElement";
import StyledButton from "../components/StyledButton";
import LinkButton from "../components/LinkButton";
import CtaBanner from "../components/Banners/CtaBanner";
import HireSpaceModal from "../components/Modal/HireSpaceModal";
import { toggleHireSpaceModal } from "../redux/actions/modalActions";
import { colors } from "../utils/theme";
import { device } from "../utils/breakpoints";
import { contact } from "../urls";
import multiData from "../data/products/multifunctionele-ruimte.json";
import maakData from "../data/products/maak.json";
import flexData from "../data/products/flexwerken.json";
import studioData from "../data/products/vaste-unit.json";
import faqItems from "../data/products/ruimtes-overzicht-faq.json";

const MUTED = "#5C6353";
const GLASS = "rgba(255,252,245,.62)";
const GLASS_HOVER = "rgba(255,252,245,.9)";

const spaces = [multiData, maakData, flexData, studioData].map(
  (product) => product.overview,
);

const RuimtesOverzicht = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState("kiezen");
  const [preselectedSpaceId, setPreselectedSpaceId] = useState(null);

  const openBookingModal = (spaceId) => {
    setPreselectedSpaceId(spaceId || null);
    dispatch(toggleHireSpaceModal());
  };

  return (
    <HomeLayout>
      <MetaTags
        title={"Ruimtes"}
        description={
          "Vier ruimtes in één pand vol makers: van een lege zaal van 90 m² tot je eigen studio met sleutel. Per dagdeel, per maand of vast."
        }
        path={"/ruimtes"}
      />

      <HeroSection>
        <StyleElement
          src={"/assets/images/illustrations/illustratie-18.svg"}
          alt={""}
          left
          bottom={-20}
          style={{ transform: "scaleX(1.3) scaleY(1.3)", opacity: 0.4 }}
        />
        <StyleElement
          src={"/assets/images/illustrations/illustratie-18.svg"}
          alt={""}
          right={-14}
          bottom={-15}
          style={{ transform: "scaleX(-1.5) scaleY(1.5)", opacity: 0.4 }}
        />
        <Container>
          <HeroContent>
            <Kicker>Ruimte huren in Amersfoort</Kicker>
            <Title>Ruimte om te maken</Title>
            <Subtitle>
              Vier ruimtes in één pand vol makers: van een lege zaal van 90 m²
              tot je eigen studio met sleutel. Per dagdeel, per maand of vast.
            </Subtitle>
          </HeroContent>
        </Container>
      </HeroSection>

      <Section>
        <Container>
          <CardsGrid>
            {spaces.map((space, index) => (
              <Card
                key={index}
                role={"link"}
                tabIndex={0}
                onClick={() => navigate(space.primary_cta.to)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    navigate(space.primary_cta.to);
                  }
                }}
              >
                <CardImage src={space.image} alt={space.image_alt} />
                <CardBody>
                  <CardHeaderRow>
                    <CardTitle>{space.name}</CardTitle>
                    <CardTag>{space.tag}</CardTag>
                  </CardHeaderRow>
                  <CardSummary>{space.summary}</CardSummary>
                  <SpecsList>
                    {space.specs.map((spec, specIndex) => (
                      <SpecRow key={specIndex}>
                        <SpecLabel>{spec.label}</SpecLabel>
                        <SpecValue>{spec.value}</SpecValue>
                      </SpecRow>
                    ))}
                  </SpecsList>
                  <CardButtons>
                    <LinkButton to={space.primary_cta.to} small>
                      {space.primary_cta.label}
                    </LinkButton>
                    {!!space.secondary_cta &&
                      (space.secondary_cta.action === "book" ? (
                        <StyledButton
                          type={"outlined"}
                          onClick={(event) => {
                            event.stopPropagation();
                            openBookingModal(space.secondary_cta.space_id);
                          }}
                        >
                          {space.secondary_cta.label}
                        </StyledButton>
                      ) : (
                        <LinkButton to={contact} type={"outlined"} small>
                          {space.secondary_cta.label}
                        </LinkButton>
                      ))}
                  </CardButtons>
                </CardBody>
              </Card>
            ))}
          </CardsGrid>
          <PriceNote>Alle bedragen zijn exclusief 21% btw.</PriceNote>
        </Container>
      </Section>

      <FaqSection>
        <StyleElement
          src={"/assets/images/illustrations/illustratie-13.svg"}
          alt={""}
          right={-8}
          bottom={-10}
        />
        <Container>
          <FaqInner>
            <FaqTitle>Veelgestelde vragen</FaqTitle>
            <FaqSubtitle>
              Duizend vragen, hier staan de antwoorden. Staat die van jou er
              niet bij, mail ons.
            </FaqSubtitle>
            <FaqList>
              {faqItems.map((item) => {
                const isOpen = openFaq === item.key;
                return (
                  <FaqCard
                    key={item.key}
                    onClick={() => setOpenFaq(isOpen ? "" : item.key)}
                  >
                    <FaqRow>
                      <FaqQuestion>{item.question}</FaqQuestion>
                      <FaqToggle $open={isOpen}>{isOpen ? "−" : "+"}</FaqToggle>
                    </FaqRow>
                    {isOpen && <FaqAnswer>{item.answer}</FaqAnswer>}
                  </FaqCard>
                );
              })}
            </FaqList>
            <FaqClosingCard>
              <FaqClosingContent>
                <FaqClosingTitle>Staat je vraag er niet bij?</FaqClosingTitle>
                <FaqClosingText>
                  Mail ons wat je van plan bent, dan zeggen we welke ruimte past
                  en wat je nodig hebt.
                </FaqClosingText>
              </FaqClosingContent>
              <LinkButton to={contact} small>
                Stel je vraag
              </LinkButton>
            </FaqClosingCard>
          </FaqInner>
        </Container>
      </FaqSection>

      <Section>
        <Container>
          <AvailabilityBanner>
            <div>
              <AvailabilityTitle>
                Nog niet zeker welke ruimte je nodig hebt?
              </AvailabilityTitle>
              <AvailabilityText>
                Stuur ons wat je van plan bent, dan zeggen we welke ruimte past
                en wat je nodig hebt. Laat je gegevens achter in het
                aanvraagformulier en we komen zo snel mogelijk bij je terug.
              </AvailabilityText>
              <ButtonRow>
                <StyledButton
                  type={"primary"}
                  onClick={() => openBookingModal(null)}
                >
                  Bekijk beschikbaarheid
                </StyledButton>
                <CreamOutlineLink to={contact} type={"outlined"}>
                  Contact opnemen
                </CreamOutlineLink>
              </ButtonRow>
              <Footnote>
                Liever eerst rondkijken? Plan een bezichtiging via{" "}
                <EmailHighlight>aanvragen@oostwest.online</EmailHighlight>
              </Footnote>
            </div>
            <AvailabilityImageWrap>
              <AvailabilityImage
                src={
                  "/assets/images/ruimte/gezamelijke-ruimte-presentatie-1.jpg"
                }
                alt={
                  "De gezamenlijke ruimte van OostWest, klaargezet voor publiek"
                }
              />
            </AvailabilityImageWrap>
          </AvailabilityBanner>
        </Container>
      </Section>

      <CtaBanner light />
      <HireSpaceModal preselectedSpaceId={preselectedSpaceId} />
    </HomeLayout>
  );
};

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;

  @media ${device.lg} {
    padding: 0 48px;
  }
`;

const Section = styled.div`
  position: relative;
  padding: 32px 0 40px;
  overflow: hidden;

  @media ${device.lg} {
    padding: 12px 0 60px;
  }
`;

const HeroSection = styled.div`
  position: relative;
  padding: 48px 0 24px;

  @media ${device.lg} {
    padding: 88px 0 36px;
  }
`;

const HeroContent = styled.div`
  max-width: 820px;
  margin: 0 auto;
  text-align: center;
  position: relative;
`;

const Kicker = styled.div`
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.secondary};
  margin-bottom: 18px;
`;

const Title = styled.h1`
  font-size: 32px;
  line-height: 1.08;
  font-weight: 800;
  text-transform: uppercase;
  color: ${colors.header};
  margin: 0 auto 20px;

  @media ${device.lg} {
    font-size: 52px;
    line-height: 1.02;
    max-width: 16ch;
  }
`;

const Subtitle = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: ${colors.text};
  margin: 0 auto;

  @media ${device.lg} {
    font-size: 17px;
    line-height: 1.65;
    max-width: 52ch;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media ${device.lg} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Card = styled.div`
  background: ${GLASS};
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    background 0.18s ease;

  &:hover {
    background: ${GLASS_HOVER};
    transform: translateY(-4px);
    box-shadow: 0 14px 30px rgba(4, 36, 16, 0.14);
  }

  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.secondary};
    outline-offset: 3px;
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;
  mask-image: url(/assets/images/masks/mask-2.1.png);
  mask-repeat: no-repeat;
  mask-size: cover;
  -webkit-mask-image: url(/assets/images/masks/mask-2.1.png);
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-size: cover;

  @media ${device.lg} {
    height: 240px;
  }
`;

const CardBody = styled.div`
  padding: 22px 22px 24px;
  display: flex;
  flex-direction: column;
  flex: 1;

  @media ${device.lg} {
    padding: 24px 28px 28px;
  }
`;

const CardHeaderRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
`;

const CardTitle = styled.h3`
  font-size: 19px;
  line-height: 1.15;
  font-weight: 800;
  text-transform: uppercase;
  color: ${colors.header};
  margin: 0;

  @media ${device.lg} {
    font-size: 22px;
  }
`;

const CardTag = styled.span`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.secondary};
  white-space: nowrap;
`;

const CardSummary = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: ${colors.text};
  margin: 0 0 20px;

  @media ${device.lg} {
    font-size: 14.5px;
  }
`;

const SpecsList = styled.div`
  display: grid;
  gap: 0;
  margin-bottom: 20px;
`;

const SpecRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 11px 0;
  border-bottom: 1px solid rgba(30, 92, 50, 0.14);

  &:last-child {
    border-bottom: none;
  }
`;

const SpecLabel = styled.span`
  font-size: 13.5px;
  font-weight: 500;
  color: ${colors.text};
`;

const SpecValue = styled.span`
  font-size: 13.5px;
  font-weight: 600;
  color: ${colors.header};
  text-align: right;
`;

const CardButtons = styled.div`
  margin-top: auto;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  button,
  a {
    margin-top: 0;
  }
`;

const PriceNote = styled.p`
  font-size: 12.5px;
  line-height: 1.6;
  color: ${MUTED};
  margin: 20px 0 0;
  text-align: center;
`;

const FaqSection = styled.div`
  position: relative;
  padding: 56px 0 64px;
  overflow: hidden;

  @media ${device.lg} {
    padding: 96px 0 104px;
  }
`;

const FaqInner = styled.div`
  max-width: 780px;
  margin: 0 auto;
  position: relative;
`;

const FaqTitle = styled.h2`
  font-size: 30px;
  line-height: 1.1;
  font-weight: 800;
  color: ${colors.header};
  margin: 0 auto 14px;
  text-align: center;

  @media ${device.lg} {
    font-size: 46px;
    line-height: 1.06;
    max-width: 14ch;
  }
`;

const FaqSubtitle = styled.p`
  font-size: 14.5px;
  line-height: 1.6;
  color: ${colors.text};
  margin: 0 auto 32px;
  text-align: center;

  @media ${device.lg} {
    font-size: 16px;
    line-height: 1.65;
    max-width: 44ch;
    margin-bottom: 48px;
  }
`;

const FaqList = styled.div`
  display: grid;
  gap: 12px;
`;

const FaqCard = styled.div`
  cursor: pointer;
  background: ${GLASS};
  border-radius: 14px;
  padding: 18px 20px;
  transition: background 0.18s ease;

  &:hover {
    background: ${GLASS_HOVER};
  }

  @media ${device.lg} {
    padding: 22px 24px;
  }
`;

const FaqRow = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;

const FaqQuestion = styled.span`
  font-size: 15px;
  line-height: 1.4;
  font-weight: 600;
  color: ${colors.header};
  flex: 1;

  @media ${device.lg} {
    font-size: 16.5px;
  }
`;

const FaqToggle = styled.span`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: ${({ $open }) =>
    $open ? colors.secondary : "rgba(30,92,50,.1)"};
  color: ${({ $open }) => ($open ? "#fff" : colors.header)};
  font-size: 20px;
  font-weight: 300;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
`;

const FaqAnswer = styled.p`
  font-size: 14px;
  line-height: 1.7;
  color: ${colors.text};
  margin: 14px 0 2px;

  @media ${device.lg} {
    font-size: 14.5px;
    margin: 14px 54px 2px 0;
  }
`;

const FaqClosingCard = styled.div`
  background: ${GLASS_HOVER};
  border-radius: 14px;
  padding: 24px 22px;
  margin-top: 20px;
  display: flex;
  gap: 24px;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;

  @media ${device.lg} {
    padding: 30px 32px;
  }

  button,
  a {
    flex: none;
    margin-top: 0;
  }
`;

const FaqClosingContent = styled.div`
  flex: 1;
  min-width: 240px;
`;

const FaqClosingTitle = styled.h3`
  font-size: 17px;
  line-height: 1.3;
  font-weight: 700;
  color: ${colors.header};
  margin: 0 0 8px;

  @media ${device.lg} {
    font-size: 19px;
  }
`;

const FaqClosingText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: ${colors.text};
  margin: 0;
  max-width: 52ch;

  @media ${device.lg} {
    font-size: 14.5px;
  }
`;

const AvailabilityBanner = styled.div`
  background: ${colors.header};
  border-radius: 16px;
  padding: 40px 24px;
  box-shadow: 0 20px 40px rgba(4, 36, 16, 0.25);
  overflow: hidden;
  display: grid;
  gap: 28px;
  align-items: stretch;

  @media ${device.lg} {
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
    gap: 52px;
    padding: 72px 56px;
  }
`;

const AvailabilityTitle = styled.h2`
  font-size: 24px;
  line-height: 1.15;
  font-weight: 800;
  text-transform: uppercase;
  color: ${colors.light};
  margin: 0 0 16px;

  @media ${device.lg} {
    font-size: 34px;
    line-height: 1.08;
  }
`;

const AvailabilityText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: rgba(241, 232, 216, 0.85);
  margin: 0 0 22px;

  @media ${device.lg} {
    font-size: 15.5px;
    line-height: 1.65;
    max-width: 52ch;
    margin: 0 0 26px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

const CreamOutlineLink = styled(LinkButton).attrs({ small: true })`
  background: transparent;
  border: 1px solid rgba(241, 232, 216, 0.5);
  color: ${colors.light};

  &:hover,
  &:active,
  &:focus {
    background: rgba(241, 232, 216, 0.15);
    color: ${colors.light};
    border: 1px solid rgba(241, 232, 216, 0.5);
  }
`;

const Footnote = styled.p`
  font-size: 12px;
  line-height: 1.6;
  color: rgba(241, 232, 216, 0.7);
  margin: 22px 0 0;

  @media ${device.lg} {
    font-size: 13px;
  }
`;

const EmailHighlight = styled.span`
  border-bottom: 1px dotted ${colors.secondaryLight};
`;

const AvailabilityImageWrap = styled.div`
  position: relative;
  min-height: 220px;
  border-radius: 10px;
  overflow: hidden;
  margin: -28px -12px -28px 0;

  @media ${device.lg} {
    min-height: 320px;
    margin: -60px -44px -60px 0;
  }
`;

const AvailabilityImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export default RuimtesOverzicht;
